import { yupResolver } from '@hookform/resolvers/yup'
import Head from 'next/head'
import Image from 'next/image'
import Router, { useRouter } from 'next/router'
import { ReactElement, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation } from 'urql'
import { object, string } from 'yup'

import { LoginLayout } from '@/components/layout/web'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { SignInMutation } from '@/queries'
import Link from '@/update-monitor/Link'
import { saveAuthentication } from '@/urql-common'

import IconVisibleOff from '@/assets/icons/VisibleOff.svg'
import IconVisibleOn from '@/assets/icons/VisibleOn.svg'
import { maybeGraphQLType } from '@/utils/graphql'

type LoginFormData = {
  username: string
  password: string
}

const schema = object({
  username: string().required(),
  password: string().required(),
})

export default function SignIn() {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<LoginFormData>({
    resolver: yupResolver(schema),
  })
  const router = useRouter()
  const [result, signInMutation] = useMutation(SignInMutation)
  const registrationType = router.query.registration_type as string
  const code = router.query.code as string

  const [showPassword, setShowPassword] = useState<boolean>(false)

  useEffect(function redirectWhenSignedIn() {
    if (localStorage.getItem('accessToken')) Router.replace('/web/user-setting')
  }, [])

  const onSubmit = async ({ username, password }: LoginFormData) => {
    const { data } = await signInMutation({
      input: { email: username, password },
    })

    if (!data?.signIn?.authentication) return

    saveAuthentication(data.signIn.authentication)

    const userRole = data.signIn.userRole
    if (userRole === 'SCHOOL_ADMIN') {
      router.push('/web/school-admin')
    } else if (userRole === 'PARTNER_ADMIN') {
      router.push('/web/partner-admin')
    } else {
      router.push('/web/user-setting')
    }
  }

  const hasWrongInputError = result.data?.signIn?.errors?.some(
    (error) => maybeGraphQLType('WrongEmailOrPassword')(error) !== null
  )
  const hasAccountLockedError = result.data?.signIn?.errors?.some(
    (error) => maybeGraphQLType('AccountLockedError')(error) !== null
  )

  return (
    <>
      <Head>
        <title>ロジックラボ | ログイン</title>
      </Head>

      <div className="grid h-full place-items-center px-5">
        <div className="w-full max-w-[720px] rounded-lg bg-white px-5 pb-[46px] pt-[80px] md:pb-10">
          <Image
            src="/gApp_logo.svg"
            width={257}
            height={68}
            className="mx-auto md:h-[80px] md:w-[300px]"
            alt="LogiQlabo"
          />

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="mx-auto mt-[34px] flex w-full max-w-[320px] flex-col items-center md:mt-[18px]"
          >
            <Input
              placeholder="メールアドレス"
              error={!!errors.username}
              className="w-full"
              {...register('username', { required: true })}
            />

            <div className="relative mt-[10px] w-full">
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="パスワード"
                error={!!errors.password}
                className="w-full pr-12"
                {...register('password', { required: true })}
              />
              <button
                type="button"
                className="absolute right-2 top-2 text-secondary-text"
                aria-label={
                  showPassword ? 'パスワードを隠す' : 'パスワードを表示する'
                }
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <IconVisibleOn width={32} />
                ) : (
                  <IconVisibleOff width={32} />
                )}
              </button>
            </div>

            {hasWrongInputError && (
              <p className="mt-1 text-xs text-caution md:text-[13px]">
                アカウント名 もしくはパスワードが正しくありません。
              </p>
            )}

            {hasAccountLockedError && (
              <p className="mt-1 text-xs text-caution md:text-[13px]">
                アカウントがロックされています。一定時間経過後に再度お試しください。
              </p>
            )}

            <div className="mt-[5px] w-full">
              <Link
                className="mt-2 inline-block text-xs font-medium text-primary-100"
                href="/web/password_forgot"
              >
                パスワードを忘れた方はこちら
              </Link>
            </div>

            <Button
              type="submit"
              variant="secondary"
              className="mx-auto mt-[34px] w-[180px]"
              disabled={!isValid}
            >
              ログイン
            </Button>
          </form>

          <Link
            href={{
              pathname: '/web/agreement',
              query: {
                registration_type: registrationType,
                code,
              },
            }}
            as="/web/agreement"
            className="mt-3 block text-center text-primary-100"
          >
            新規アカウント作成
          </Link>
        </div>
      </div>
    </>
  )
}

SignIn.getLayout = (page: ReactElement) => <LoginLayout>{page}</LoginLayout>
